/**
 * StellarDateTimePicker — reusable calendar + time picker (2026-02).
 *
 * Displays combined date+time in the global CRM format:
 *   "Monday, July 7, 2026, 7:33 PM"
 * Click the field → same react-day-picker month grid the CRM uses for task
 * deadlines, plus hour / minute / AM-PM controls.
 *
 * Value contract: UTC ISO string (or '') — displayed/edited in the viewer's
 * local timezone. onChange(newIso) fires on every date/time change.
 *
 * WebKit rules inherited from StellarDatePicker (Iter 70.4): hand-rolled
 * popover only — NO Radix Popover, NO asChild trigger composition. Native
 * <select> elements are used for hour/minute (reliable inside dialogs).
 */
import * as React from 'react';
import { ChevronDown, Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { formatHeaderDate } from '../lib/dateFormat';
import { cn } from '../lib/utils';

function fmtLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${formatHeaderDate(d)}, ${t}`;
}

const SELECT_CLS =
  'bg-card border border-border/30 rounded-md text-foreground text-[12px] h-8 px-1.5 ' +
  'focus:outline-none focus:ring-1 focus:ring-gold/40 focus:border-gold/40';

export function StellarDateTimePicker({
  value,            // UTC ISO string or ''
  onChange,         // (isoString) => void
  placeholder = 'Pick date & time',
  className = '',
  disabled = false,
  testId,
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  const current = value ? new Date(value) : null;
  const valid = current && !Number.isNaN(current.getTime());

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      const root = containerRef.current;
      if (root && root.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocPointer, true);
    document.addEventListener('touchstart', onDocPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer, true);
      document.removeEventListener('touchstart', onDocPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /** Base Date for edits — the current value, else "now" rounded to minute. */
  const base = () => {
    const d = valid ? new Date(current) : new Date();
    d.setSeconds(0, 0);
    return d;
  };

  const emit = (d) => onChange?.(d.toISOString());

  const pickDate = (day) => {
    if (!day) return;
    const d = base();
    d.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    emit(d);
  };
  const pickHour12 = (h12) => {
    const d = base();
    const isPM = d.getHours() >= 12;
    d.setHours((h12 % 12) + (isPM ? 12 : 0));
    emit(d);
  };
  const pickMinute = (m) => { const d = base(); d.setMinutes(m); emit(d); };
  const pickMeridiem = (pm) => {
    const d = base();
    const h = d.getHours() % 12;
    d.setHours(pm ? h + 12 : h);
    emit(d);
  };

  const disp = base();
  const hour12 = disp.getHours() % 12 === 0 ? 12 : disp.getHours() % 12;
  const isPM = disp.getHours() >= 12;

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        style={{ touchAction: 'manipulation' }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg',
          'bg-card border border-border/30 text-foreground',
          'hover:border-border/50 focus:ring-1 focus:ring-gold/40 focus:border-gold/40 focus:outline-none',
          'transition-colors h-9 text-[12px] px-2.5',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <span className="flex items-center gap-1.5 truncate">
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
          <span className={cn('truncate', !valid && 'text-muted-foreground/40')}>
            {valid ? fmtLabel(value) : placeholder}
          </span>
        </span>
        <ChevronDown className="h-3 w-3 text-muted-foreground/40 shrink-0" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose date and time"
          className="absolute top-full left-0 mt-1 z-[100] p-0 bg-card border border-border/40 shadow-xl rounded-md"
          style={{ minWidth: 'max-content' }}
          data-testid={testId ? `${testId}-panel` : undefined}
        >
          <div className="flex items-center gap-1.5 px-3 pt-3 pb-1 border-b border-border/30">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Time</span>
            <select
              value={hour12}
              onChange={(e) => pickHour12(parseInt(e.target.value, 10))}
              className={SELECT_CLS}
              data-testid={testId ? `${testId}-hour` : undefined}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <span className="text-muted-foreground">:</span>
            <select
              value={disp.getMinutes()}
              onChange={(e) => pickMinute(parseInt(e.target.value, 10))}
              className={SELECT_CLS}
              data-testid={testId ? `${testId}-minute` : undefined}
            >
              {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
            <div className="flex rounded-md overflow-hidden border border-border/30 ml-1">
              {['AM', 'PM'].map((mer) => (
                <button
                  key={mer}
                  type="button"
                  onClick={() => pickMeridiem(mer === 'PM')}
                  className={cn(
                    'px-2 h-8 text-[11px] font-medium transition-colors',
                    (mer === 'PM') === isPM
                      ? 'bg-gold/20 text-gold'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                  data-testid={testId ? `${testId}-${mer.toLowerCase()}` : undefined}
                >
                  {mer}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto px-2.5 h-8 text-[11px] rounded-md bg-gold/15 text-gold hover:bg-gold/25 font-medium"
              data-testid={testId ? `${testId}-done` : undefined}
            >
              Done
            </button>
          </div>
          <Calendar mode="single" selected={valid ? current : undefined} onSelect={pickDate} initialFocus />
        </div>
      )}
    </div>
  );
}
